import styled, { ThemeProvider } from 'styled-components';
import theme from '../styles/theme';
import { useNavigate } from 'react-router-dom'; // Import useNavigate
import { useAuth } from '../auth/AuthContext';

const DisclaimerTitle = styled.h3`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 10px 20px;
    font-family: ${props => props.theme.font.family};
    font-size: ${props => props.theme.font.size.large};
    font-style: ${props => props.theme.font.style};
    font-weight: ${props => props.theme.font.weight.regular};
`;
const DisclaimerContainer = styled.div`  
    width: 80%;  
    margin: 0 auto;  
`;

const DisclaimerText = styled.p`
    font-family: ${props => props.theme.font.family};
    font-size: ${props => props.theme.font.size.regular};
    font-style: ${props => props.theme.font.style};
    padding: 10px 20px;
    text-align: left;
    italics: true;
`;

const DisclaimerList = styled.ul`
`;

const DisclaimerListItem = styled.li`
    font-family: ${props => props.theme.font.family};
    font-size: ${props => props.theme.font.size.regular};
    font-style: ${props => props.theme.font.style};
`;

const DisclaimerAck = styled.div`
    padding: 10px 20px;
`;

const Checkbox = styled.input`
    width: 20px;
    height: 20px;
    cursor: pointer;
`;

const CheckboxLabel = styled.label`
    font-family: ${props => props.theme.font.family};
    font-size: ${props => props.theme.font.size.regular};
    font-style: ${props => props.theme.font.style};
    padding: 10px 20px;
    text-align: left;
    bold: true;
`;

const ButtonWrapper = styled.div`
    display: flex;
    justify-content: flex-start;
    padding-top: 20px;
`;

const DisclaimerButton = styled.button<{ disabled: boolean }>`  
    font-family: ${props => props.theme.font.family};  
    font-style: ${props => props.theme.font.style};  
    border: none;  
    border-radius: .25rem;
    padding: 15px 32px;  
    margin: .5rem;  
    text-align: center;  
    text-decoration: none;  
    display: inline-block;  
    font-size: 16px;  
    opacity: ${props => (props.disabled ? 0.5 : 1)};  
    pointer-events: ${props => (props.disabled ? 'none' : 'auto')};  
    cursor: pointer;  

    &:hover {
        background-color: #ddd;
    }
`;

const Landing = () => {
    const navigate = useNavigate(); // Initialize useNavigate
    const { isAuthenticated, isAgreementChecked, toggleCheckAgreement } = useAuth();

    if (isAuthenticated) {
        navigate('/ReportSearch');
    }

    const handleCheckboxClick = () => {
        toggleCheckAgreement();
    };

    const handleLoginClick = () => {
        navigate('/Login', { state: { formState: 'login' } });
    };

    const handleRegisterClick = () => {
        navigate('/Login', { state: { formState: 'register' } });
    };

    return (
        <ThemeProvider theme={theme}>
            <DisclaimerTitle>Disclaimer and Restriction of Use</DisclaimerTitle>
            <DisclaimerContainer>
                <DisclaimerText>
                    The Lubbock Police Department is committed to collecting and disseminating police report
                    information in a timely and accurate manner.  Reports are classified based upon information supplied
                    to the Lubbock Police Department by reporting parties. The report classifications may be changed
                    following subsequent investigation.  The Lubbock Police Department does not guarantee (either
                    expressed or implied) the accuracy or completeness of the information contained in these reports.
                </DisclaimerText>
                <DisclaimerText>
                    The Lubbock Police Department is not responsible for any error or omission, or for the use
                    of, or the results obtained from, the use of this information.  Due to the preliminary nature of
                    the reports, media outlets should make a diligent effort to confirm any information prior to
                    reporting it.  Confirmation can be made by contacting the Public Information Unit, the appropriate
                    investigative division, or other departmental authorities.
                </DisclaimerText>
                <DisclaimerText>
                    The Lubbock Police Department believes that providing open and simple access to police
                    reports online reinforces our commitment to transparency.  However, in disseminating this
                    information we must also comply with current laws that regulate the release of potentially
                    sensitive and confidential information.  To ensure that private information is protected and the
                    standards delineated by the Texas Public Information Act are met, certain information is
                    automatically redacted and some reports are not available on the site.  The exclusions include, but
                    are not limited to:
                </DisclaimerText>
                <DisclaimerList>
                    <DisclaimerListItem>Sexually oriented offenses
                    </DisclaimerListItem>
                    <DisclaimerListItem>Offenses where juveniles (individuals under 17 years of age) are the complainant
                    </DisclaimerListItem>
                    <DisclaimerListItem>Reference to individuals considered suspects and witnesses
                    </DisclaimerListItem>
                    <DisclaimerListItem>Detailed descriptions of property items considered evidence
                    </DisclaimerListItem>
                    <DisclaimerListItem>Crash reports, which may be obtained here: <a href="https://policereports.lexisnexis.com">policereports.lexisnexis.com</a>
                    </DisclaimerListItem>
                </DisclaimerList>



                <DisclaimerText>
                    Individuals who believe that additional or more complete information disclosure is
                    warranted are encouraged to submit an open records request, per state law.
                </DisclaimerText>
                <DisclaimerText>
                    Open records requests must be made in writing.  For full information and instructions see:
                    <a href="https://www.mylubbock.us/pages/public-information-act">www.mylubbock.us/pages/public-information-act</a>
                </DisclaimerText>

                <DisclaimerText>
                    The user specifically acknowledges that the Lubbock Police Department is not responsible for
                    any defamatory, offensive, misleading, or illegal conduct of other users, links, or third parties
                    and that the risk of injury from the foregoing rests entirely with the user.  The unauthorized use
                    of the words, "Lubbock Police Department" or the Lubbock Police Department logo or any colorable
                    imitation of either is unlawful.  This web page does not, in any way, authorize such use.
                </DisclaimerText>
                <DisclaimerAck>
                    <Checkbox type="checkbox" checked={isAgreementChecked} onChange={handleCheckboxClick} /><CheckboxLabel>I attest that I have read and understood the provisions stated above.</CheckboxLabel>
                    <ButtonWrapper>
                        <DisclaimerButton disabled={!isAgreementChecked} onClick={handleLoginClick}>
                            Login
                        </DisclaimerButton>
                        <DisclaimerButton disabled={!isAgreementChecked} onClick={handleRegisterClick}>
                            Register
                        </DisclaimerButton>
                    </ButtonWrapper>
                </DisclaimerAck>
            </DisclaimerContainer>
        </ThemeProvider>
    );
}

export default Landing;
